import {
  useGetCurrentUserQuery,
  useGetRevisionsQuery,
  useRevertDemoMutation,
} from '@carboncopyco/data';
import { RevertOutlineIcon } from '@carboncopyco/icons';
import {
  PermissionAction,
  PermissionKey,
  type Revision,
} from '@carboncopyco/types';
import { Button, ConfirmAlert, notify } from '@carboncopyco/ui';
import { AnalyticsTrackEventName, useAnalytics } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useState } from 'react';
import { useIsDemoLocked } from '../../../../hooks';
import { AssetLockedModal } from '../../../AssetLockedModal';
import { usePermissionsContext } from '../../../utils';
import { useDemoPageProps } from '../DemoPageContext';

export const RevertButton = ({ revision }: { revision: Revision }) => {
  const flags = useFlags();
  const { trackEvent } = useAnalytics();
  const { currRevision } = revision;
  const [openRevertModal, setOpenRevertModal] = useState(false);
  const [isDisabled, setIsDisabled] = useBoolean();
  const { organizationId, demoId } = useDemoPageProps();
  const { data } = useGetRevisionsQuery({
    organizationId,
    sourceType: 'demo',
    id: demoId,
    reverseOrder: true,
    limit: 100,
  });
  const revisions = data?.items;
  const isLastRevision = revisions?.length === currRevision + 1;
  const { getPermissions } = usePermissionsContext();
  const [revertDemo] = useRevertDemoMutation();

  const [isDemoLockedModal, setIsDemoLockedModal] = useState(false);
  const { data: userData } = useGetCurrentUserQuery();

  const { checkIsDemoLocked, userName } = useIsDemoLocked({
    demoId,
    organizationId,
    currentUserEmail: userData.user.email,
    shouldRunInterval: false,
  });

  if (isLastRevision) {
    return null;
  }

  const revertDemoPermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.revertDemo
  );
  return (
    <div className="flex flex-row">
      {revertDemoPermission && (
        <Button
          dark
          icon={<RevertOutlineIcon />}
          onClick={async () => {
            trackEvent(AnalyticsTrackEventName.DEMO_CLICK_REVERT, {
              location: 'demo_info_page_history_tab_table_side_actions',
            });

            if (flags.editorMultiUserLocking) {
              const lockedBy = await checkIsDemoLocked();
              if (lockedBy) {
                setIsDemoLockedModal(true);

                trackEvent(AnalyticsTrackEventName.DEMO_MULTI_USER_LOCK, {
                  location: 'demo_info_page_history_tab_table_side_actions',
                  variables: {
                    userEditing: lockedBy,
                    userTryingToEdit: userData.user.email,
                  },
                  demoId,
                });

                return;
              }
            }

            setOpenRevertModal(true);
          }}
        />
      )}
      {isDemoLockedModal && (
        <AssetLockedModal
          userName={userName}
          buttonLabel="Okay"
          onButtonClick={() => {
            setIsDemoLockedModal(false);
          }}
        />
      )}

      <ConfirmAlert
        disabled={isDisabled}
        title="Revert to old revision?"
        containerClass="w-ds-modal-560"
        onConfirm={() => {
          trackEvent(AnalyticsTrackEventName.DEMO_CLICK_REVERT_APPROVE, {
            location: 'demo_info_page_revert_to_old_revision_modal',
          });
          setIsDisabled.setTrue();
          revertDemo({
            organizationId,
            revisionId: String(currRevision),
            id: demoId,
          })
            .unwrap()
            .then(() => {
              notify.success('Success reverting demo');
            })
            .catch(() => {
              notify.error('Error reverting demo');
            })
            .finally(() => {
              setOpenRevertModal(false);
              setIsDisabled.setFalse();
            });
        }}
        onClose={() => {
          trackEvent(AnalyticsTrackEventName.DEMO_CLICK_REVERT_CANCEL, {
            location: 'demo_info_page_revert_to_old_revision_modal',
          });
          setOpenRevertModal(false);
        }}
        show={openRevertModal}
        description={`A new revision of this demo will be created with the content of revision #${
          currRevision + 1
        }`}
        noSecondButton
        yesLabel="Revert"
      />
    </div>
  );
};
